import React, { useState, useEffect } from 'react'
import './FilteredDropdown.css'

type FilteredDropdownProps<T> = {
  items: T[]
  labelKey1: keyof T
  labelKey2: keyof T
  value: string
  onChange: (value: string) => void
  onSelect: (selectedItem: T) => void
  placeholder?: string
  dropdownClassName?: string
  showIcon?: boolean
  iconColor?: string
  formatDisplay?: (item: T) => string
}

const FilteredDropdown = <T extends Record<string, any>>({
  items,
  labelKey1,
  labelKey2,
  value,
  onChange,
  onSelect,
  placeholder = 'Search...',
  dropdownClassName = '',
  showIcon = false,
  iconColor = '#8A8E97',
  formatDisplay,
}: FilteredDropdownProps<T>): JSX.Element => {
  const [filteredItems, setFilteredItems] = useState<T[]>([])
  const [showDropdown, setShowDropdown] = useState<boolean>(false)

  useEffect(() => {
    if (value.length > 0) {
      const filtered = items.filter((item) =>
        `${item[labelKey1]} ${item[labelKey2]}`
          .toLowerCase()
          .includes(value.toLowerCase()),
      )
      setFilteredItems(filtered)
      setShowDropdown(true)
    } else {
      setFilteredItems([])
      setShowDropdown(false)
    }
  }, [value, items, labelKey1, labelKey2])

  const handleSelect = (item: T): void => {
    const displayName = formatDisplay
      ? formatDisplay(item)
      : `${item[labelKey1]} ${item[labelKey2]}`

    onChange(displayName)
    onSelect(item)
    setShowDropdown(false)
  }

  return (
    <div className="filtered-dropdown-component position-relative">
      <div className="d-flex align-items-center position-relative">
        {showIcon && (
          <i
            className="fa-solid fa-magnifying-glass me-2"
            style={{ position: 'absolute', left: '10px', color: iconColor }}
          ></i>
        )}
        <input
          type="text"
          value={value}
          placeholder={placeholder}
          className="form-control"
          onChange={(e) => onChange(e.target.value)}
          style={{ paddingLeft: showIcon ? '30px' : '10px' }}
        />
      </div>

      {showDropdown && filteredItems.length > 0 && (
        <div className={`dropdown-menu show ${dropdownClassName}`}>
          {filteredItems.map((item, index) => (
            <div
              key={index}
              className="dropdown-item"
              onClick={() => handleSelect(item)}
              style={{ cursor: 'pointer' }}
            >
              {formatDisplay
                ? formatDisplay(item)
                : `${item[labelKey1]} ${item[labelKey2]}`}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

export default FilteredDropdown
